import jsQR from 'jsqr';

/**
 * Reading a QR symbol out of a camera frame.
 *
 * <p>
 * <strong>Installed rather than written, which is the opposite call from the encoder beside it.</strong>
 * `format/qr.ts` says why it was hand-written: what an encoder needs is a bit stream, a Reed–Solomon
 * remainder and a module grid, and the packages arrive with canvas shims around them. A decoder is
 * not the mirror of that. It has to binarise a photograph under shop lighting, find three finder
 * patterns in it, correct the perspective of a tag held at an angle, and only then run the error
 * correction — and getting that wrong does not fail loudly, it misreads a serial and puts the wrong
 * garment on the counter. `jsqr` is the small, dependency-free, pure-function form of that work: it
 * takes pixels and returns text, with no DOM of its own, which is the objection the encoder's note
 * actually raises.
 * </p>
 *
 * <p>
 * <strong>The native `BarcodeDetector` is deliberately not used, even where it exists.</strong> It
 * is absent from Chrome on Windows — which is the machine on the counter — so it could only ever be
 * a second path for phones, and two decoders reading the same tag is two behaviours to keep
 * matched. One path, everywhere.
 * </p>
 */

/**
 * The largest frame worth decoding.
 *
 * A 1080p webcam frame is two million pixels to binarise, thirty times a second, and the symbol on
 * a garment tag survives being read at a fraction of that. Downscaling is what keeps the loop from
 * making the whole page stutter while the panel is open.
 */
export const maxScanEdge = 640;

/** The scale to draw a frame at so its longest edge is at most {@link maxScanEdge}. */
export function scanScale(width: number, height: number): number {
  const longest = Math.max(width, height);

  return longest > maxScanEdge ? maxScanEdge / longest : 1;
}

/**
 * What a scanned payload means to this product.
 *
 * The QR on a docket carries the bare serial, so this is mostly a trim — but a scanner can append a
 * newline or a carriage return of its own, and a payload that later becomes a URL should still
 * yield the serial rather than being looked up whole. Anything after the last slash wins, which
 * leaves a bare serial untouched.
 */
export function scannedSerial(payload: string): string | null {
  const trimmed = payload.trim().replace(/[\r\n]+$/, '');

  if (trimmed === '') {
    return null;
  }

  const lastSegment = trimmed.slice(trimmed.lastIndexOf('/') + 1).trim();

  return lastSegment === '' ? null : lastSegment;
}

/**
 * Reads one frame. Null when there is no symbol in it, which is almost every frame.
 *
 * `dontInvert` because a printed tag is dark on light and trying the inverse doubles the work on
 * every frame that has nothing in it — which, while somebody lines a tag up, is all of them.
 */
export function decodeFrame(data: Uint8ClampedArray, width: number, height: number): string | null {
  const found = jsQR(data, width, height, { inversionAttempts: 'dontInvert' });

  return found === null ? null : scannedSerial(found.data);
}
